import * as React from 'react';
import { cn } from '../cn.js';
import { Tabs as TabsPrimitive } from 'radix-ui';

/**
 * As abas — mecânica do Radix, aparência da casa.
 *
 * ## Por que é primitivo e não um `useState` com três botões
 *
 * Aba tem navegação por teclado com regra própria: as setas andam entre as
 * abas **sem** ativar o painel, `Home`/`End` vão às pontas, `Tab` sai da barra
 * e entra no conteúdo, e o `aria-controls`/`aria-labelledby` amarra os dois
 * lados. É a fronteira que o `DESIGN.md` descreve — camada, foco preso ou
 * navegação por teclado — e escrever isso à mão é a semana inteira para chegar
 * pior.
 *
 * ## O que foi trocado do que a CLI escreveu
 *
 * O arquivo gerado vinha com duas variantes (pílula e linha), `text-sm`,
 * `text-foreground/60`, anel de foco próprio e um `after:` absoluto para o
 * sublinhado. Sobrou **uma** anatomia, a do desenho de 05/09: sublinhado de
 * 2px na aba ativa, peso 700, e a barra separada do conteúdo por uma borda.
 *
 * Uma e não duas porque a segunda não tinha tela: variante sem uso é a mesma
 * dívida que componente parado, e a primeira tela que precisasse da pílula
 * escolheria entre duas anatomias sem nada dizendo qual.
 *
 * O anel de foco sai do `:focus-visible` global do `theme.css`, como no
 * `Button` — o do shadcn desenhava um segundo anel com outra cor.
 */
function Tabs({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn('flex flex-col', className)}
      {...props}
    />
  );
}

function TabsList({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      /*
       * `rolagem-lateral` e não quebra de linha: com quatro abas e um telefone
       * estreito, quebrar empurra o conteúdo para baixo e a barra deixa de ser
       * uma linha — o utilitário do `theme.css` faz a rolagem se anunciar.
       */
      className={cn('rolagem-lateral flex gap-0.5 border-b border-borda px-2', className)}
      {...props}
    />
  );
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        'whitespace-nowrap border-b-2 border-transparent px-3 py-3 text-[0.8125rem] font-medium text-texto-suave',
        'transition-[color,border-color] hover:text-texto',
        'data-[state=active]:border-primaria-texto data-[state=active]:font-bold data-[state=active]:text-texto',
        /*
         * A aba que não abre: cinza e sem clique (23/09/2026, a ficha de quem
         * não aderiu à demanda). Sem `pointer-events-none`, de propósito — o
         * `title` com o motivo precisa do ponteiro para aparecer.
         */
        'disabled:cursor-not-allowed disabled:text-texto-tenue disabled:hover:text-texto-tenue',
        // 44px onde há dedo, como todo controle. Ver a régua do DESIGN.md.
        'pointer-coarse:min-h-11',
        className,
      )}
      {...props}
    />
  );
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn('outline-none', className)}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
